import React from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
	faCheckCircle,
	faExternalLinkAlt,
	faLock,
	faLockOpen,
	faLongArrowAltDown,
	faTruck,
} from "@fortawesome/free-solid-svg-icons"
import { COLORS } from "../../utilities/Constants"
import { generalStyles as gs } from "../styles/styles"
import {
	DEPANNAGE_TYPE_APPEL_POLICE,
	DIAGNOSTIQUE_POLICE_LABELS,
} from "../../utilities/Addons"
import {
	coordsCarte,
	getLieuCarte,
	positionIntrouvable,
} from "../../utilities/depLieux"
import CollapsibleCardV2 from "../basics/interface/CollapsibleCardV2"
import PointInteretSelectV2 from "../others/PointInteretSelectV2"
import { BadgeOrdreLivraison } from "./assistances/FicheAssistanceBloc"
import CarteTrajetV2 from "./CarteTrajetV2"

const st = {
	// Trajet à gauche, carte à droite (P9) ; sans coordonnées, la colonne
	// gauche prend toute la largeur.
	grille: (avecCarte) => ({
		display: "grid",
		gridTemplateColumns: avecCarte
			? "minmax(0, 1fr) minmax(0, 1fr)"
			: "minmax(0, 1fr)",
		columnGap: 24,
		alignItems: "stretch",
	}),
	fleche: {
		display: "block",
		color: COLORS.textLight,
		fontSize: 26,
		margin: "20px 0 20px 13px",
	},
	// Pastille devant chaque libellé = légende des marqueurs de la carte.
	pastille: (couleur) => ({
		display: "inline-flex",
		alignItems: "center",
		justifyContent: "center",
		width: 16,
		height: 16,
		borderRadius: "50%",
		backgroundColor: couleur,
		color: "#fff",
		fontSize: 9,
		fontWeight: 700,
		letterSpacing: 0,
		marginRight: 7,
		verticalAlign: "middle",
		flexShrink: 0,
	}),
	micro: {
		fontSize: 10,
		fontWeight: 600,
		color: COLORS.textLight,
		letterSpacing: "0.08em",
		textTransform: "uppercase",
		marginBottom: 8,
		display: "flex",
		alignItems: "center",
	},
	// Bloc d'adresse en lecture seule : bordure PLEINE (le pointillé est
	// réservé à la destination « Inconnue »).
	bloc: {
		padding: "12px 14px",
		borderRadius: 10,
		border: `1px solid ${COLORS.border}`,
		backgroundColor: COLORS.surface,
		minHeight: 64,
		boxSizing: "border-box",
	},
	blocInconnue: {
		padding: "12px 14px",
		borderRadius: 10,
		border: `1.5px dashed ${COLORS.warning}`,
		backgroundColor: COLORS.warningLight,
		minHeight: 64,
		boxSizing: "border-box",
	},
	enTeteBloc: {
		display: "flex",
		alignItems: "center",
		gap: 8,
		minWidth: 0,
	},
	type: {
		flexShrink: 0,
		padding: "2px 8px",
		borderRadius: 99,
		fontSize: 11,
		fontWeight: 600,
		color: COLORS.accent,
		backgroundColor: COLORS.accentLight,
	},
	titre: {
		fontSize: 15,
		fontWeight: 600,
		color: COLORS.text,
		overflow: "hidden",
		textOverflow: "ellipsis",
		whiteSpace: "nowrap",
		minWidth: 0,
	},
	enTeteGps: {
		display: "flex",
		alignItems: "center",
		flexWrap: "wrap",
		gap: "2px 8px",
	},
	titreGps: {
		fontSize: 15,
		fontWeight: 600,
		color: COLORS.text,
		overflowWrap: "anywhere",
	},
	ligne: { fontSize: 13, color: COLORS.textMuted, marginTop: 2 },
	introuvable: {
		marginLeft: 8,
		fontSize: 11.5,
		color: COLORS.textLight,
	},
	vide: { fontSize: 14, color: COLORS.textLight },
	maps: {
		display: "inline-flex",
		alignItems: "center",
		gap: 5,
		marginTop: 8,
		fontSize: 12,
		fontWeight: 600,
		color: COLORS.accent,
		textDecoration: "none",
	},
	meta: {
		display: "flex",
		gap: 6,
		marginTop: 8,
		fontSize: 12.5,
		color: COLORS.textMuted,
	},
	metaLabel: { color: COLORS.textLight },
	// Vignettes de fichiers DANS le bloc du point (test 06.10) : texte à
	// gauche, vignettes 50×40 alignées à droite, en ligne (défilement
	// horizontal si trop nombreuses). La marge négative compense celle des
	// vignettes (3 px).
	blocContenu: { display: "flex", alignItems: "flex-start", gap: 12 },
	blocTexte: { flex: "1 1 auto", minWidth: 0 },
	vignettes: {
		flex: "0 1 auto",
		minWidth: 0,
		maxWidth: "50%",
		display: "flex",
		flexWrap: "nowrap",
		overflowX: "auto",
		margin: "-3px -3px 0 0",
	},
	vignette: { flexShrink: 0 },
	// Fichiers d'une livraison : lignes « Avant » / « Après » sous l'adresse.
	lignesFichiers: {
		display: "flex",
		flexDirection: "column",
		gap: 6,
		marginTop: 8,
	},
	ligneFichiers: { display: "flex", alignItems: "center", gap: 10 },
	ligneFichiersLibelle: {
		width: 44,
		flexShrink: 0,
		fontSize: 11,
		fontWeight: 600,
		color: COLORS.textLight,
		letterSpacing: "0.06em",
		textTransform: "uppercase",
	},
	vignettesLigne: { display: "flex", flexWrap: "wrap", gap: 0 },
	// Livraison : rangées Avant / Après dans la colonne de droite du bloc, à
	// la place des vignettes en ligne du lieu et de la destination.
	colonneLignes: {
		flex: "0 1 auto",
		minWidth: 0,
		maxWidth: "50%",
		display: "flex",
		flexDirection: "column",
		alignItems: "flex-end",
		gap: 6,
		margin: "-3px -3px 0 0",
	},
	ligneFichiersCompacte: {
		display: "flex",
		alignItems: "center",
		gap: 8,
		maxWidth: "100%",
	},
	vignettesLigneCompacte: {
		display: "flex",
		flexWrap: "nowrap",
		overflowX: "auto",
		minWidth: 0,
	},
	cadenas: (couleur) => ({
		...gs.iconBtn(couleur),
		width: 28,
		height: 28,
		fontSize: 12,
	}),
}

// Bloc d'adresse riche (P1) : type, label, adresse 2 lignes, lien Maps —
// visible par tous (le lien n'est plus réservé à l'édition). `droite` : contenu
// de la colonne de droite à la place des vignettes en ligne (rangées Avant /
// Après d'une livraison, Jordan 08.10).
const BlocAdresse = ({ po, fichiers, rendreFichier, droite = null }) => {
	const lieu = getLieuCarte(po)
	// Coordonnées GPS en titre : sous la pastille plutôt que tronquées à
	// rien quand la colonne est étroite (vignettes, mobile).
	const gps = !!lieu && lieu.type === "GPS"
	if (!lieu) {
		return (
			<div style={st.bloc}>
				<AvecVignettes
					fichiers={fichiers}
					rendreFichier={rendreFichier}
					droite={droite}
				>
					<span style={st.vide}>Non renseigné</span>
				</AvecVignettes>
			</div>
		)
	}
	return (
		<div style={st.bloc}>
			<AvecVignettes
				fichiers={fichiers}
				rendreFichier={rendreFichier}
				droite={droite}
			>
				<div style={gps ? st.enTeteGps : st.enTeteBloc}>
					{lieu.type && <span style={st.type}>{lieu.type}</span>}
					<span
						style={gps ? st.titreGps : st.titre}
						title={lieu.titre}
					>
						{lieu.titre}
					</span>
				</div>
				{lieu.lignes.map((l) => (
					<div key={l} style={st.ligne}>
						{l}
					</div>
				))}
				{lieu.mapsUrl && (
					<a
						href={lieu.mapsUrl}
						target="_blank"
						rel="noopener noreferrer"
						style={st.maps}
					>
						Ouvrir dans Maps
						<FontAwesomeIcon
							icon={faExternalLinkAlt}
							style={{ fontSize: 10 }}
						/>
					</a>
				)}
				{lieu.estimee && (
					<span
						style={st.introuvable}
						title="Adresse déduite des coordonnées GPS (OpenStreetMap) : indicative."
					>
						· adresse estimée
					</span>
				)}
				{positionIntrouvable(po) && (
					<span
						style={st.introuvable}
						title="Adresse non trouvée par le géocodage : ce point n'apparaît pas sur la carte."
					>
						· position introuvable
					</span>
				)}
			</AvecVignettes>
		</div>
	)
}

// Destination « Sur place » (P3) : même gabarit que le bloc d'adresse.
const BlocSurPlace = ({ fichiers, rendreFichier }) => (
	<div style={st.bloc}>
		<AvecVignettes fichiers={fichiers} rendreFichier={rendreFichier}>
			<div style={st.enTeteBloc}>
				<FontAwesomeIcon
					icon={faCheckCircle}
					style={{ color: COLORS.success }}
				/>
				<span style={st.titre}>Sur place</span>
			</div>
			<div style={st.ligne}>Le véhicule n'a pas été transporté.</div>
		</AvecVignettes>
	</div>
)

// Destination « Inconnue » verrouillée (P3) : teinte warning, comme la
// pastille « Inconnue » du sélecteur.
const BlocInconnue = ({ fichiers, rendreFichier }) => (
	<div style={st.blocInconnue}>
		<AvecVignettes fichiers={fichiers} rendreFichier={rendreFichier}>
			<div style={{ ...st.titre, color: COLORS.warning }}>Inconnue</div>
			<div style={st.ligne}>À saisir sur le terrain.</div>
		</AvecVignettes>
	</div>
)

const Meta = ({ label, valeur }) =>
	valeur ? (
		<div style={st.meta}>
			<span style={st.metaLabel}>{label}</span>
			<span style={{ color: COLORS.text }}>{valeur}</span>
		</div>
	) : null

// Couleurs des marqueurs = légende de la colonne gauche.
const COULEUR_LIEU = COLORS.accent
const COULEUR_DESTINATION = COLORS.success
const COULEUR_LIVRAISON = COLORS.textMuted

// Livraisons en ordre chronologique, n° = index serveur (comme la bande de
// mission, Tarifs et Fichiers).
const livraisonsChrono = (dep) =>
	(dep.livraisons || [])
		.map((liv, index) => ({ liv, numero: index + 1 }))
		.sort((a, b) =>
			(a.liv.datetime || "9999").localeCompare(b.liv.datetime || "9999"),
		)

// Points de la carte : seuls ceux dont les coordonnées sont connues
// (coordsCarte : GPS, parcs géocodés, adresses géocodées par le serveur).
// « Sur place » / « Inconnue » : pas de destination.
const pointsCarte = (dep, destinationInconnue, livraisons) => {
	const points = []
	const ajouter = (po, couleur, titre, numero) => {
		const c = coordsCarte(po)
		if (c) points.push({ ...c, couleur, titre, numero })
	}
	ajouter(dep.lieu_intervention, COULEUR_LIEU, "Lieu d'intervention")
	if (!dep.surPlace && !destinationInconnue)
		ajouter(dep.destination_finale, COULEUR_DESTINATION, "Destination")
	livraisons.forEach(({ liv, numero }) =>
		ajouter(
			liv.destination_adresse,
			COULEUR_LIVRAISON,
			`Livraison ${numero}`,
			numero,
		),
	)
	return points
}

// En-tête (P2) : cadenas discret à la place du gros bouton. Boss →
// déverrouille lieu ET destination ; non-boss → icône seule + tooltip.
const cadenasEnTete = ({
	verrouille,
	deverrouillable,
	isBoss,
	onDeverrouiller,
	onVerrouiller,
}) => {
	if (!deverrouillable) return null
	// Re-verrouiller = retour à l'affichage lecture seule ; les modifications
	// en cours restent dans la fiche (à sauvegarder comme les autres).
	if (!verrouille) {
		return (
			<button
				type="button"
				style={st.cadenas(COLORS.accent)}
				onClick={onVerrouiller}
				title="Reverrouiller le lieu et la destination"
			>
				<FontAwesomeIcon icon={faLockOpen} />
			</button>
		)
	}
	if (isBoss) {
		return (
			<button
				type="button"
				style={st.cadenas(COLORS.textMuted)}
				onClick={onDeverrouiller}
				title="Déverrouiller la modification du lieu et de la destination"
			>
				<FontAwesomeIcon icon={faLock} />
			</button>
		)
	}
	return (
		<span
			style={{
				...st.cadenas(COLORS.textLight),
				cursor: "default",
			}}
			title="Modification réservée au boss."
		>
			<FontAwesomeIcon icon={faLock} />
		</span>
	)
}

const corpsDestination = ({
	dep,
	verrouille,
	destinationInconnue,
	onInconnu,
	setDestinationFinale,
	rendreFichier,
}) => {
	const fichiers = fichiersDe(dep, null, FICHIERS_APRES)
	if (dep.surPlace)
		return (
			<BlocSurPlace fichiers={fichiers} rendreFichier={rendreFichier} />
		)
	if (verrouille) {
		return destinationInconnue ? (
			<BlocInconnue fichiers={fichiers} rendreFichier={rendreFichier} />
		) : (
			<BlocAdresse
				po={dep.destination_finale}
				fichiers={fichiers}
				rendreFichier={rendreFichier}
			/>
		)
	}
	return (
		<>
			<PointInteretSelectV2
				pointInteret={dep.destination_finale}
				updatePointInteret={setDestinationFinale}
				inputStyle={gs.input}
				inconnu={destinationInconnue}
				onInconnu={onInconnu}
				inconnuHint="La destination n'est pas renseignée sur cette fiche."
			/>
			{/* Garde-fou : la carte Fichiers n'existe plus. */}
			<LignesFichiers apres={fichiers} rendreFichier={rendreFichier} />
		</>
	)
}

// Fichiers sous un point du trajet — seul endroit où ils s'affichent depuis
// la suppression de la carte Fichiers (08.10) : lieu = avant (photos 0,
// vidéos 3), destination = après (1, 4), livraison = deux lignes Avant / Après.
// Carte grise (2) : carte Véhicule.
const FICHIERS_AVANT = [0, 3]
const FICHIERS_APRES = [1, 4]
const fichiersDe = (dep, livraisonId, types) =>
	(dep.files || []).filter(
		(f) => f.livraison === livraisonId && types.includes(f.fileType),
	)

// Garde-fou (suppression de la carte Fichiers, 08.10) : un fichier avant /
// après rattaché à une livraison absente de la fiche ne doit pas disparaître.
const fichiersOrphelins = (dep) => {
	const ids = new Set((dep.livraisons || []).map((l) => l.id))
	const orphelins = (dep.files || []).filter(
		(f) =>
			f.livraison !== null &&
			f.livraison !== undefined &&
			!ids.has(f.livraison),
	)
	return {
		avant: orphelins.filter((f) => FICHIERS_AVANT.includes(f.fileType)),
		apres: orphelins.filter((f) => FICHIERS_APRES.includes(f.fileType)),
	}
}

// Contenu d'un bloc + ses vignettes à droite. `rendreFichier(file, index)`
// vient de la fiche (vignette 50×40 + visionneuse). `droite` remplace la
// rangée de vignettes (livraison : rangées Avant / Après).
const AvecVignettes = ({ fichiers, rendreFichier, droite = null, children }) =>
	droite ? (
		<div style={st.blocContenu}>
			<div style={st.blocTexte}>{children}</div>
			{droite}
		</div>
	) : rendreFichier && fichiers && fichiers.length > 0 ? (
		<div style={st.blocContenu}>
			<div style={st.blocTexte}>{children}</div>
			<div style={st.vignettes}>
				{fichiers.map((f, i) => (
					<div key={f.id || i} style={st.vignette}>
						{rendreFichier(f, i)}
					</div>
				))}
			</div>
		</div>
	) : (
		children
	)

// Fichiers d'une livraison (Jordan 08.10) : deux lignes distinctes « Avant »
// puis « Après », vignettes 50×40 + visionneuse ; une ligne vide est masquée.
// `compacte` : dans la colonne de droite du bloc d'adresse (même place que les
// vignettes du lieu et de la destination).
const LignesFichiers = ({
	avant = [],
	apres = [],
	rendreFichier,
	compacte = false,
}) => {
	if (!rendreFichier || (!avant.length && !apres.length)) return null
	return (
		<div style={compacte ? st.colonneLignes : st.lignesFichiers}>
			{[
				["Avant", avant],
				["Après", apres],
			]
				.filter(([, fichiers]) => fichiers.length > 0)
				.map(([libelle, fichiers]) => (
					<div
						key={libelle}
						style={
							compacte
								? st.ligneFichiersCompacte
								: st.ligneFichiers
						}
					>
						<span style={st.ligneFichiersLibelle}>{libelle}</span>
						<div
							style={
								compacte
									? st.vignettesLigneCompacte
									: st.vignettesLigne
							}
						>
							{fichiers.map((f, i) => (
								<div key={f.id || i} style={st.vignette}>
									{rendreFichier(f, i)}
								</div>
							))}
						</div>
					</div>
				))}
		</div>
	)
}

const libelleSecteur = (dep, secteursPolice) => {
	if (!(dep.secteur_police > 0)) return ""
	const secteur = (secteursPolice || []).find(
		(s) => s.id === dep.secteur_police,
	)
	return secteur ? secteur.label : ""
}

/**
 * Carte « Trajet » de la fiche dépannage (refonte 06.10) : à gauche lieu
 * d'intervention ↓ destination ↓ livraisons, à droite une carte Leaflet
 * de tous les points connus (P9). Lecture seule
 * riche quand les lieux sont verrouillés (soft-lock 12.08), sélecteurs
 * sinon (fiche neuve, déverrouillage boss).
 */
const DepFicheTrajetV2 = ({
	dep,
	verrouille,
	isBoss,
	onDeverrouiller,
	onVerrouiller,
	destinationInconnue,
	onInconnu,
	setLieuIntervention,
	setDestinationFinale,
	secteursPolice,
	rendreFichier,
	open,
	onToggle,
}) => {
	const isPolice = dep.appel_type === DEPANNAGE_TYPE_APPEL_POLICE
	const livraisons = livraisonsChrono(dep)
	const orphelins = fichiersOrphelins(dep)
	const points = pointsCarte(dep, destinationInconnue, livraisons)
	const diagnosticPolice = isPolice
		? DIAGNOSTIQUE_POLICE_LABELS[dep.diagnostique_info?.diagnostique_police]
		: ""
	return (
		<CollapsibleCardV2
			title="Trajet"
			open={open}
			onToggle={onToggle}
			status={destinationInconnue && !dep.surPlace ? "incomplete" : null}
			right={cadenasEnTete({
				verrouille,
				deverrouillable: dep.id !== 0,
				isBoss,
				onDeverrouiller,
				onVerrouiller,
			})}
		>
			<div style={st.grille(points.length > 0)}>
				<div>
					<div style={st.micro}>
						<span style={st.pastille(COULEUR_LIEU)} />
						Lieu d'intervention
					</div>
					{verrouille ? (
						<BlocAdresse
							po={dep.lieu_intervention}
							fichiers={fichiersDe(dep, null, FICHIERS_AVANT)}
							rendreFichier={rendreFichier}
						/>
					) : (
						<>
							<PointInteretSelectV2
								pointInteret={dep.lieu_intervention}
								updatePointInteret={setLieuIntervention}
								inputStyle={gs.input}
							/>
							{/* Garde-fou : la carte Fichiers n'existe plus. */}
							<LignesFichiers
								avant={fichiersDe(dep, null, FICHIERS_AVANT)}
								rendreFichier={rendreFichier}
							/>
						</>
					)}
					<Meta
						label="Arrivée sur le lieu"
						valeur={dep.timestamp_1}
					/>
					{isPolice && (
						<Meta
							label="Secteur"
							valeur={libelleSecteur(dep, secteursPolice)}
						/>
					)}
					<FontAwesomeIcon
						icon={faLongArrowAltDown}
						style={st.fleche}
					/>
					<div style={st.micro}>
						<span style={st.pastille(COULEUR_DESTINATION)} />
						Destination
					</div>
					{corpsDestination({
						dep,
						verrouille,
						destinationInconnue,
						onInconnu,
						setDestinationFinale,
						rendreFichier,
					})}
					<Meta
						label="Arrivée à destination"
						valeur={dep.timestamp_3}
					/>
					<Meta
						label="Etat d'intervention police"
						valeur={diagnosticPolice}
					/>
					{livraisons.map(({ liv, numero }) => (
						<div key={`trajet_liv_${liv.id}`}>
							<FontAwesomeIcon
								icon={faLongArrowAltDown}
								style={st.fleche}
							/>
							<div style={st.micro}>
								<span style={st.pastille(COULEUR_LIVRAISON)}>
									{numero}
								</span>
								<FontAwesomeIcon
									icon={faTruck}
									style={{ marginRight: 5 }}
								/>
								{[
									`Livraison ${numero}`,
									[liv.date, liv.heure]
										.filter(Boolean)
										.join(" "),
								]
									.filter(Boolean)
									.join(" — ")}
								{/* GE-57 : n° d'ordre de l'assistance (facturation). */}
								<BadgeOrdreLivraison
									depId={dep.id}
									livraisonId={liv.id}
								/>
							</div>
							{/* Fichiers Avant / Après DANS le cadre de la
							    livraison, comme le lieu et la destination
							    (Jordan 08.10). */}
							<BlocAdresse
								po={liv.destination_adresse}
								droite={
									<LignesFichiers
										compacte
										avant={fichiersDe(
											dep,
											liv.id,
											FICHIERS_AVANT,
										)}
										apres={fichiersDe(
											dep,
											liv.id,
											FICHIERS_APRES,
										)}
										rendreFichier={rendreFichier}
									/>
								}
							/>
						</div>
					))}
					{orphelins.avant.length + orphelins.apres.length > 0 && (
						<div>
							<div style={{ ...st.micro, marginTop: 14 }}>
								Autres fichiers
							</div>
							<LignesFichiers
								avant={orphelins.avant}
								apres={orphelins.apres}
								rendreFichier={rendreFichier}
							/>
						</div>
					)}
				</div>
				{points.length > 0 && <CarteTrajetV2 points={points} />}
			</div>
		</CollapsibleCardV2>
	)
}

export default DepFicheTrajetV2
